<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="stockDetailsTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="stockDetailsTitle">
            Lagergerät {{ device?.stock_number ?? '' }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Schließen"
          ></button>
        </div>

        <div v-if="device" class="modal-body">
          <!-- Zwei Spalten: links das Geraet selbst (Stammdaten, IMEI-Infos), rechts
               alles, was mit dem Servicepartner zu tun hat (Stand, Dateien,
               Lieferscheine). Auf schmalen Anzeigen untereinander. -->
          <div class="row g-4">
            <div class="col-lg-6">
              <!-- Manueller Abschluss: oben, weil er alles andere überholt - das
                   Geraet ist nicht mehr im Lager. -->
              <template v-if="device.closing">
                <h6 class="detail-block-title">Abschluss</h6>
                <dl class="detail-fields">
                  <div class="detail-field">
                    <dt>Art</dt>
                    <dd>{{ device.closing.type.description }}</dd>
                  </div>
                  <div class="detail-field">
                    <dt>Wert</dt>
                    <dd>
                      <template v-if="device.closing.value_net_eur !== null">{{ formatEur(device.closing.value_net_eur) }} netto</template>
                      <span v-else class="detail-muted">—</span>
                    </dd>
                  </div>
                  <template v-if="device.closing.hs_receipt">
                    <div class="detail-field">
                      <dt>Artikel</dt>
                      <dd>
                        {{ device.closing.hs_receipt.shop_device_label }}
                        <span v-if="device.closing.hs_receipt.shop_device_condition" class="detail-muted">· {{ device.closing.hs_receipt.shop_device_condition }}</span>
                      </dd>
                    </div>
                    <div class="detail-field">
                      <dt>Bestellung</dt>
                      <dd>
                        <template v-if="device.closing.hs_receipt.supplier_order_id !== null">#{{ device.closing.hs_receipt.supplier_order_id }} · Repaircenter</template>
                        <span v-else class="detail-muted">—</span>
                      </dd>
                    </div>
                    <div class="detail-field">
                      <dt>Lieferschein</dt>
                      <dd>
                        <template v-if="device.closing.hs_receipt.hs_delivery_slip_number">{{ device.closing.hs_receipt.hs_delivery_slip_number }} · an HS Zentrale</template>
                        <span v-else class="detail-muted">—</span>
                      </dd>
                    </div>
                  </template>
                  <template v-if="device.closing.ankauf">
                    <div class="detail-field">
                      <dt>Grading</dt>
                      <dd>{{ device.closing.ankauf.grading ?? '—' }}</dd>
                    </div>
                    <div class="detail-field">
                      <dt>Rechnung</dt>
                      <dd>
                        <template v-if="device.closing.ankauf.invoice_number">{{ device.closing.ankauf.invoice_number }} · an Assona</template>
                        <span v-else class="detail-muted">—</span>
                      </dd>
                    </div>
                    <div class="detail-field">
                      <dt>E-Nummer</dt>
                      <dd>{{ device.closing.ankauf.e_number ?? '—' }}</dd>
                    </div>
                    <div class="detail-field">
                      <dt>Lieferscheine</dt>
                      <dd>
                        <template v-if="device.closing.ankauf.hs_delivery_slip_numbers.length">{{ device.closing.ankauf.hs_delivery_slip_numbers.join(' → ') }} · an den Ankauf</template>
                        <span v-else class="detail-muted">—</span>
                      </dd>
                    </div>
                  </template>
                  <div v-if="device.closing.note" class="detail-field">
                    <dt>Notiz</dt>
                    <dd class="partner-note-text">{{ device.closing.note }}</dd>
                  </div>
                  <div class="detail-field">
                    <dt>Abgeschlossen</dt>
                    <dd>{{ device.closing.user_name }} <span class="detail-muted">· {{ formatDate(device.closing.creation_timestamp) }}</span></dd>
                  </div>
                </dl>
                <!-- Lagereingang HS auf halber Strecke abgebrochen: das Gerät ist
                     abgeschlossen, die Buchung wird fortgesetzt, nicht wiederholt. -->
                <div v-if="device.closing.hs_receipt && !device.closing.hs_receipt.completed" class="alert alert-warning py-2">
                  <strong>Einbuchung unvollständig.</strong>
                  <span v-if="device.closing.hs_receipt.error"> {{ device.closing.hs_receipt.error }}</span>
                  <div class="mt-2">
                    <button type="button" class="btn btn-sm btn-warning" @click="choose('resume-hs-receipt')">Einbuchung fortsetzen</button>
                  </div>
                </div>
                <!-- Ankauf HS.cc auf halber Strecke abgebrochen: fortsetzen legt keine zweite
                     Rechnung an — eine vorhandene wird übernommen oder gesperrt. -->
                <div v-if="device.closing.ankauf && !device.closing.ankauf.completed" class="alert alert-warning py-2">
                  <strong>Ankauf unvollständig.</strong>
                  <span v-if="device.closing.ankauf.error"> {{ device.closing.ankauf.error }}</span>
                  <div class="mt-2">
                    <button type="button" class="btn btn-sm btn-warning" @click="choose('resume-hs-receipt')">Ankauf fortsetzen</button>
                  </div>
                </div>
              </template>

              <h6 class="detail-block-title">Geräteinformationen</h6>
              <dl class="detail-fields">
                <div class="detail-field">
                  <dt>Gerät</dt>
                  <dd v-if="device.is_legacy_device">
                    {{ device.master.description || '—' }}
                    <span class="badge bg-secondary">Altgerät</span>
                  </dd>
                  <dd v-else>
                    <router-link
                      :to="{ name: 'masterarticle', params: { id: device.master.id } }"
                      target="_blank"
                    >{{ device.master.description || '—' }}</router-link>
                    <span v-if="device.vendor.description" class="detail-muted"> · {{ device.vendor.description }}</span>
                  </dd>
                </div>
                <template v-if="!device.is_legacy_device">
                <div class="detail-field">
                  <dt>Farbe</dt>
                  <dd>{{ device.color?.description || '—' }}</dd>
                </div>
                <div class="detail-field">
                  <dt>Speicher</dt>
                  <dd>{{ device.memory_size_label || '—' }}</dd>
                </div>
                </template>
                <div class="detail-field">
                  <dt>Zustand</dt>
                  <dd>{{ device.condition ? device.condition.description : '—' }}</dd>
                </div>
                <!-- Hier steht die IMEI, die das Gerät JETZT hat. Wurde sie einmal
                     gewechselt (SWAP beim Servicepartner), führt der Knopf daneben zu
                     den früheren — im Normalfall gibt es keine, und dann steht auch
                     kein Knopf da. -->
                <div class="detail-field">
                  <dt>IMEI / SN</dt>
                  <dd class="detail-imei">
                    <span class="detail-mono">{{ device.imei_sn || '—' }}</span>
                    <button
                      v-if="device.previous_imeis.length"
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      :title="device.previous_imeis.length === 1
                        ? 'Diese IMEI wurde einmal gewechselt'
                        : `Diese IMEI wurde ${device.previous_imeis.length}× gewechselt`"
                      @click="choose('imei-history')"
                    >Andere IMEIs</button>
                  </dd>
                </div>
                <div class="detail-field">
                  <dt>Ticket</dt>
                  <dd>
                    <router-link
                      v-if="device.ticket"
                      :to="{ name: 'ticket-detail', params: { id: device.ticket.id } }"
                      target="_blank"
                    >{{ device.ticket.order_number }}</router-link>
                    <span v-else class="detail-muted">ohne Ticket</span>
                  </dd>
                </div>
                <div class="detail-field">
                  <dt>Eingebucht</dt>
                  <dd>{{ formatDate(device.creation_timestamp) }}</dd>
                </div>
                <div class="detail-field">
                  <dt>Status</dt>
                  <dd>
                    <span class="stock-status" :style="statusStyle">
                      {{ device.status.description }}
                      <span v-if="device.status.changed_timestamp" class="stock-status-when">
                        · {{ formatDate(device.status.changed_timestamp) }}
                      </span>
                    </span>
                  </dd>
                </div>
              </dl>

              <!-- IMEI-Informationen wie im Ticket: FMIP-Stand und die Detailauskunft
                   der Schnittstelle. „Anzeigen" liest Gespeichertes, „Abfragen" kostet
                   Guthaben — die Beschriftung sagt vorher, was der Klick auslöst. -->
              <template v-if="device.imei_sn">
                <h6 class="detail-block-title">IMEI-Informationen</h6>
                <dl class="detail-fields">
                  <div class="detail-field">
                    <dt>FMIP-Check</dt>
                    <dd class="fmip-dd">
                      <span class="fmip" :class="`fmip-${device.fmip?.status ?? 'unknown'}`">{{ fmipLabel }}</span>
                      <span v-if="device.fmip?.checked_at" class="fmip-checked">
                        zuletzt geprüft: {{ formatDate(device.fmip.checked_at) }}
                      </span>
                    </dd>
                  </div>
                  <div v-if="device.fmip?.device_description" class="detail-field">
                    <dt>Gerät laut IMEI</dt>
                    <dd class="imei-check-device">{{ device.fmip.device_description }}</dd>
                  </div>
                  <div class="detail-field">
                    <dt>IMEI Details</dt>
                    <dd>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        @click="choose('imei-information')"
                      >{{ device.has_imei_information ? 'Anzeigen' : 'Abfragen' }}</button>
                    </dd>
                  </div>
                </dl>
              </template>
            </div>

            <div class="col-lg-6">
              <h6 class="detail-block-title">Servicepartner</h6>
              <dl class="detail-fields">
                <!-- „Nächstes Ziel" ist eine Aussage über die Zukunft. Ist das Gerät
                     beim Partner angekommen, gibt es keine mehr — dann steht hier
                     nichts, obwohl der Versand für den Etikett-Nachdruck bleibt. -->
                <div class="detail-field">
                  <dt>Nächstes Ziel</dt>
                  <dd>
                    <template v-if="device.next_destination">
                      {{ device.next_destination.servicepartner.description || '—' }}
                      <span class="detail-muted">
                        · versandt {{ formatDate(device.next_destination.creation_timestamp) }}
                      </span>
                    </template>
                    <span v-else class="detail-muted">—</span>
                  </dd>
                </div>

                <template v-if="device.partner_case">
                  <!-- Preisstand: Bezugspreis, ob bestaetigt, offene Anfrage, letzte
                       Entscheidung. Wer wann was getan hat, steht im Verlauf. -->
                  <div class="detail-field">
                    <dt>Preis</dt>
                    <dd>
                      <div class="partner-cost">
                        <span class="cost-badge" :class="costBadgeClass">{{ costStateLabel }}</span>
                        <strong>{{ cost.reference_price === null ? 'kein Preis hinterlegt' : cost.reference_price_label }}</strong>
                        <span v-if="cost.approved_amount !== null && cost.price_request?.decision === 'accepted'" class="detail-muted">
                          zugesagt {{ formatDate(cost.price_request.decision_timestamp) }}
                        </span>
                      </div>
                      <!-- Weicht der bestätigte Betrag vom heutigen Preis ab, wurde die
                           Rezeptur nach der Bestätigung geändert. Die Einigung gilt
                           trotzdem — deshalb Auskunft und keine Warnung. -->
                      <div
                        v-if="cost.confirmed_timestamp && cost.amount !== cost.reference_price"
                        class="partner-cost-note"
                      >
                        bestätigt wurde {{ cost.amount_label }}
                      </div>
                      <div v-if="cost.price_request && cost.price_request.decision === null" class="partner-cost-note partner-request">
                        Anfrage: <strong>{{ cost.price_request.amount_label }}</strong>
                        <span class="detail-muted"> · {{ formatDate(cost.price_request.timestamp) }}</span>
                        <div class="partner-note-text">{{ cost.price_request.note }}</div>
                      </div>
                      <div v-else-if="cost.price_request && cost.price_request.decision === 'rejected'" class="partner-cost-note">
                        Anfrage über {{ cost.price_request.amount_label }} abgelehnt
                        <span class="detail-muted">· {{ formatDate(cost.price_request.decision_timestamp) }}</span>
                        <div v-if="cost.price_request.decision_note" class="partner-note-text">{{ cost.price_request.decision_note }}</div>
                      </div>
                    </dd>
                  </div>
                  <div class="detail-field">
                    <dt>Referenz</dt>
                    <dd>
                      <template v-if="device.partner_case.partner_reference">{{ device.partner_case.partner_reference }}</template>
                      <span v-else class="detail-muted">—</span>
                    </dd>
                  </div>
                  <div class="detail-field">
                    <dt>Fertig bis</dt>
                    <dd>
                      <template v-if="device.partner_case.expected_completion_date">{{ formatDay(device.partner_case.expected_completion_date) }}</template>
                      <span v-else class="detail-muted">—</span>
                    </dd>
                  </div>
                  <!-- Reparaturergebnis samt Text: was der Partner beim Abschluss
                       eingetragen hat. -->
                  <div class="detail-field">
                    <dt>Ergebnis</dt>
                    <dd>
                      <template v-if="device.partner_case.outcome">
                        <span class="cost-badge" :class="device.partner_case.outcome === 'repaired' ? 'is-approved' : 'is-rejected'">
                          {{ device.partner_case.outcome === 'repaired'
                            ? (device.partner_case.grading ? `repariert · Grading ${device.partner_case.grading}` : 'repariert')
                            : 'nicht repariert' }}
                        </span>
                        <span v-if="device.partner_case.completed_timestamp" class="detail-muted">
                          · {{ formatDate(device.partner_case.completed_timestamp) }}
                        </span>
                        <div v-if="device.partner_case.outcome_note" class="partner-note-text">{{ device.partner_case.outcome_note }}</div>
                      </template>
                      <span v-else class="detail-muted">noch offen</span>
                    </dd>
                  </div>
                </template>
                <div v-else class="detail-field">
                  <dt>Beim Partner</dt>
                  <dd><span class="detail-muted">noch nicht übernommen</span></dd>
                </div>
              </dl>

              <!-- Dokumente aus dem Portal, je Art - auch wenn nichts da ist, damit
                   man sieht, was fehlt. -->
              <h6 class="detail-block-title">Dateien</h6>
              <dl class="detail-fields">
                <div v-for="group in fileGroups" :key="group.category" class="detail-field">
                  <dt>{{ group.label }}</dt>
                  <dd>
                    <template v-if="group.files.length">
                      <div v-for="file in group.files" :key="file.id" class="slip-row">
                        <span>{{ file.description }}</span>
                        <span class="detail-muted">{{ formatDate(file.creation_timestamp) }}</span>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-secondary"
                          :disabled="fileBusyId !== null"
                          @click="onOpenPartnerFile(file)"
                        >{{ fileBusyId === file.id ? 'Lädt …' : 'Ansehen' }}</button>
                      </div>
                    </template>
                    <span v-else class="detail-muted">keine</span>
                  </dd>
                </div>
                <div v-if="fileError" class="error slip-error">{{ fileError }}</div>
              </dl>

              <h6 class="detail-block-title">Lieferscheine</h6>
              <dl class="detail-fields">
                <div class="detail-field">
                  <dt>Lieferschein</dt>
                  <dd>
                    <template v-if="device.delivery_slips.length">
                      <div v-for="slip in device.delivery_slips" :key="slip.id" class="slip-row">
                        <span class="slip-number">{{ slip.slip_number }}</span>
                        <span class="detail-muted">{{ formatDate(slip.creation_timestamp) }}</span>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-secondary"
                          :disabled="pdfBusyId !== null"
                          @click="onOpenPdf(slip)"
                        >{{ pdfBusyId === slip.id ? 'Lädt …' : 'PDF' }}</button>
                      </div>
                      <div v-if="pdfError" class="error slip-error">{{ pdfError }}</div>
                    </template>
                    <span v-else class="detail-muted">auf keinem Lieferschein</span>
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <!-- Aktionen zum Gerät. Bewusst hier und nicht in der Liste: sie gelten
               immer für genau dieses Gerät, und man sieht dabei, was man vor sich
               hat. Ausgelöst wird erst, wenn dieses Modal zu ist — sonst hängt der
               Backdrop des Folge-Modals. -->
          <section class="detail-actions">
            <h6 class="detail-actions-title">Aktionen</h6>
            <div class="detail-actions-list">
              <button
                type="button"
                class="btn btn-outline-primary detail-action-btn"
                :disabled="!canShip"
                :title="canShip ? undefined : shipBlockedReason"
                @click="choose('servicepartner')"
              >Versand an Servicepartner</button>

              <!-- Nachdruck nur, solange das Geraet vorbereitet oder unterwegs ist -
                   sobald der Partner es hat (oder es zurueck ist), ist das Etikett
                   wertlos. -->
              <button
                type="button"
                class="btn btn-outline-primary detail-action-btn"
                :disabled="!canReprint"
                :title="canReprint ? undefined : reprintBlockedReason"
                @click="choose('reprint')"
              >Versandetikett nachdrucken</button>

              <!-- Storno nur, solange das Gerät noch vorbereitet ist. Steht es auf
                   einem Lieferschein, ist es unterwegs. -->
              <button
                type="button"
                class="btn btn-outline-danger detail-action-btn"
                :disabled="!canCancel"
                :title="canCancel ? undefined : cancelBlockedReason"
                @click="choose('cancel')"
              >Versand stornieren</button>

              <!-- Korrektur der Gerätedaten: falscher Master, falsche Farbe, falsche
                   Speichergröße. Immer erlaubt — was nicht geht (Masterwechsel bei
                   vorbereitetem Versand), lehnt der Server im Klartext ab. -->
              <button
                type="button"
                class="btn detail-action-btn detail-action-correct"
                :disabled="isClosed"
                :title="isClosed ? CLOSED_HINT : undefined"
                @click="choose('correct')"
              >Gerät korrigieren</button>

              <!-- Manueller Abschluss: nur solange das Gerät bei uns liegt.
                   Endgültig; nur „Lagereingang HS" bucht zusätzlich in Zeus. -->
              <button
                type="button"
                class="btn btn-outline-primary detail-action-btn"
                :disabled="!canClose"
                :title="canClose ? undefined : closeBlockedReason"
                @click="choose('close')"
              >Manueller Abschluss</button>

              <button
                type="button"
                class="btn btn-outline-secondary detail-action-btn"
                @click="choose('history')"
              >Verlauf</button>
            </div>
            <!-- Abgeschlossen: ein Satz statt drei Sperrgruenden. -->
            <p v-if="isClosed" class="detail-actions-hint">{{ CLOSED_HINT }}</p>
            <template v-else>
              <p v-if="!canShip" class="detail-actions-hint">{{ shipBlockedReason }}</p>
              <p v-if="!canReprint" class="detail-actions-hint">{{ reprintBlockedReason }}</p>
              <p v-if="!canClose" class="detail-actions-hint">{{ closeBlockedReason }}</p>
            </template>
          </section>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-primary" data-bs-dismiss="modal">Schließen</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import {
  fetchDeliverySlipPdfBlob,
  fetchPartnerCaseFileBlob,
  isClosableStatus,
  isReprintableStatus,
  isShippableStatus,
  STOCK_STATUS_ABGESCHLOSSEN,
  type StockDevice,
  type StockDeviceDeliverySlip,
  type StockDevicePartnerCost,
  type StockDevicePartnerFile,
} from '../services/stockDevices';

/**
 * Eckdaten eines Lagergeräts samt der Aktionen zu diesem Gerät — zweispaltig:
 * links Geräte- und IMEI-Informationen, rechts der Stand beim Servicepartner,
 * die Dateien und die Lieferscheine.
 *
 * `open(device)` liefert die gewählte Aktion — oder null, wenn nur geschaut wurde.
 * Aufgelöst wird erst im `hidden`-Event: das Folge-Modal darf erst aufgehen, wenn
 * dieses wirklich zu ist, sonst bleibt ein Backdrop hängen.
 *
 * Alle Angaben stecken bereits in der Liste, es wird nichts nachgeladen.
 */
export type StockDeviceAction = 'servicepartner' | 'reprint' | 'cancel' | 'correct' | 'close' | 'resume-hs-receipt' | 'history' | 'imei-history' | 'imei-information';

const CLOSED_HINT = 'Das Gerät ist abgeschlossen und kann nicht mehr verändert werden.';

const NO_SHIPMENT_HINT = 'Zu diesem Gerät ist kein Versand angelegt — es gibt kein Etikett '
  + 'zum Nachdrucken und nichts zu stornieren.';

const FILE_CATEGORIES: Array<{ category: string; label: string }> = [
  { category: 'testbericht',     label: 'Testbericht' },
  { category: 'reparaturschein', label: 'Reparaturschein' },
  { category: 'rechnung',        label: 'Rechnung' },
];

const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;
let   resolver: ((action: StockDeviceAction | null) => void) | null = null;
let   chosen: StockDeviceAction | null = null;

const device     = ref<StockDevice | null>(null);
const pdfBusyId  = ref<number | null>(null);
const pdfError   = ref<string | null>(null);
const fileBusyId = ref<number | null>(null);
const fileError  = ref<string | null>(null);

/** Preisblock des Falls — nur gültig, wenn `device.partner_case` gesetzt ist. */
const cost = computed<StockDevicePartnerCost>(() => device.value!.partner_case!.cost);

const costStateLabel = computed<string>(() => {
  switch (device.value?.partner_case?.cost.state) {
    case 'confirmed': return 'Preis bestätigt';
    case 'requested': return 'Warte auf Preisfreigabe';
    default:          return 'Preis nicht bestätigt';
  }
});

const costBadgeClass = computed<string>(() => {
  switch (device.value?.partner_case?.cost.state) {
    case 'confirmed': return 'is-approved';
    case 'requested': return 'is-pending';
    default:          return '';
  }
});

const fmipLabel = computed<string>(() => {
  switch (device.value?.fmip?.status) {
    case 'active':   return 'aktiv';
    case 'inactive': return 'nicht aktiv';
    default:         return 'unbekannt';
  }
});

/** Dateien je Art — auch leere Arten, damit man sieht, was fehlt. */
const fileGroups = computed(() => FILE_CATEGORIES.map(group => ({
  ...group,
  files: (device.value?.partner_case?.files ?? []).filter(f => f.category === group.category),
})));

const statusStyle = computed<Record<string, string> | undefined>(() => {
  const s = device.value?.status;
  if (!s || (!s.background_color && !s.foreground_color)) return undefined;
  return {
    background: s.background_color || 'var(--accent-soft, rgba(247, 166, 0, 0.10))',
    color:      s.foreground_color || 'var(--accent-hover, #e09600)',
  };
});

// Hinaus darf ein Gerät im Status „Neu" und ein Rückläufer vom Servicepartner —
// sonst ist es schon unterwegs oder liegt beim Partner.
const canShip = computed<boolean>(() => isShippableStatus(device.value?.status.id));

const shipBlockedReason = computed<string>(() =>
  `Das Gerät ist im Status „${device.value?.status.description ?? '?'}" und kann nicht `
  + 'versandt werden.');

const hasShipment = computed<boolean>(() => device.value?.shipment != null);

// Nachdruck: Versand vorhanden UND Geraet noch nicht beim Partner.
const canReprint = computed<boolean>(() => hasShipment.value && isReprintableStatus(device.value?.status.id));

const reprintBlockedReason = computed<string>(() => {
  if (!hasShipment.value) return NO_SHIPMENT_HINT;
  return 'Das Gerät ist beim Servicepartner angekommen bzw. zurück — das Versandetikett hat keinen Zweck mehr.';
});

// Storniert wird nur ein vorbereiteter Versand — einer, der noch auf keinem
// Lieferschein steht.
const canCancel = computed<boolean>(() => device.value?.shipment?.is_active === true);

const cancelBlockedReason = computed<string>(() => hasShipment.value
  ? 'Der Versand steht schon auf einem Lieferschein und kann nicht mehr storniert werden.'
  : NO_SHIPMENT_HINT);

// Abgeschlossen = eingefroren: nur noch lesen (Verlauf, IMEI-Auskunft).
const isClosed = computed<boolean>(() => device.value?.status.id === STOCK_STATUS_ABGESCHLOSSEN);

// Abschließen nur, solange das Gerät bei uns liegt.
const canClose = computed<boolean>(() => isClosableStatus(device.value?.status.id));

const closeBlockedReason = computed<string>(() => isClosed.value
  ? CLOSED_HINT
  : `Das Gerät ist im Status „${device.value?.status.description ?? '?'}" und liegt nicht bei uns — `
    + 'abschließen geht nur bei „Neu" oder „Vom Servicepartner zurück".');

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modal?.dispose();
  modal = null;
});

function open(item: StockDevice): Promise<StockDeviceAction | null> {
  device.value     = item;
  pdfBusyId.value  = null;
  pdfError.value   = null;
  fileBusyId.value = null;
  fileError.value  = null;
  chosen           = null;
  return new Promise<StockDeviceAction | null>(resolve => {
    resolver = resolve;
    modal?.show();
  });
}

function choose(action: StockDeviceAction): void {
  chosen = action;
  modal?.hide();
}

function onHidden(): void {
  resolver?.(chosen);
  resolver = null;
}

/**
 * Öffnet den Lieferschein in einem neuen Tab. Der Endpunkt braucht ein Token,
 * deshalb der Umweg über den Blob statt eines einfachen Links.
 */
async function onOpenPdf(slip: StockDeviceDeliverySlip): Promise<void> {
  if (pdfBusyId.value !== null) return;
  pdfBusyId.value = slip.id;
  pdfError.value  = null;
  try {
    const url = URL.createObjectURL(await fetchDeliverySlipPdfBlob(slip.id));
    window.open(url, '_blank');
    // URL nach kurzer Zeit freigeben (der Tab hat sie dann geladen).
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    pdfError.value = e instanceof Error ? e.message : 'Das PDF konnte nicht geladen werden.';
  } finally {
    pdfBusyId.value = null;
  }
}

/**
 * Öffnet ein Partner-Dokument in einem neuen Tab. Wie beim Lieferschein-PDF
 * braucht der Endpunkt ein Token, deshalb der Umweg über den Blob.
 */
async function onOpenPartnerFile(file: StockDevicePartnerFile): Promise<void> {
  if (fileBusyId.value !== null || device.value === null) return;
  fileBusyId.value = file.id;
  fileError.value  = null;
  try {
    const url = URL.createObjectURL(await fetchPartnerCaseFileBlob(device.value.id, file.id));
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    fileError.value = e instanceof Error ? e.message : 'Das Dokument konnte nicht geladen werden.';
  } finally {
    fileBusyId.value = null;
  }
}

/**
 * Reines Datum (`2026-09-25`) ohne Uhrzeit. Bewusst als Textumbau und NICHT über
 * `Date`: `new Date('2026-09-25')` ist UTC-Mitternacht und würde in unserer
 * Zeitzone eine Uhrzeit erfinden, die nie eingegeben wurde.
 */
function formatEur(value: number): string {
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format(value);
}

function formatDay(value: string | null): string {
  if (!value) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (m === null) return value;
  const [, year, month, day] = m;
  return `${day}.${month}.${year}`;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}

defineExpose({ open });
</script>

<style scoped>
.detail-fields {
  margin: 0;
}

.detail-block-title {
  margin: 0 0 0.4rem;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6c757d;
}

/* Zweiter und weitere Bloecke einer Spalte bekommen Luft nach oben. */
.detail-fields + .detail-block-title {
  margin-top: 1.1rem;
}

.detail-mono {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
}

/* IMEI und der Knopf zu den früheren stehen in einer Zeile. */
.detail-imei {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* Kosten des Partners: Zustand, Betrag und unser Preis in einer Zeile. */
.partner-cost {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.partner-cost-note {
  margin-top: 4px;
  font-size: 13px;
}

.partner-request {
  padding: 4px 8px;
  border-radius: 6px;
  background: rgba(247, 166, 0, 0.10);
}

/* Freitexte des Partners (Begruendung, Reparaturinformation, Ablehnungsgrund). */
.partner-note-text {
  margin-top: 2px;
  white-space: pre-wrap;
  color: #343a40;
}

.cost-badge {
  display: inline-block;
  padding: 1px 9px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.06);
}

.cost-badge.is-pending { background: rgba(247, 166, 0, 0.18); color: #8a5c00; }
.cost-badge.is-approved { background: rgba(25, 135, 84, 0.14); color: #146c43; }
.cost-badge.is-rejected { background: rgba(215, 0, 21, 0.10); color: #b3000f; }

/* Status-Badge wie in der Lagerliste. */
.stock-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 9999px;
  background: var(--accent-soft, rgba(247, 166, 0, 0.10));
  color: var(--accent-hover, #e09600);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.stock-status-when {
  font-weight: 500;
  opacity: 0.75;
  font-variant-numeric: tabular-nums;
}

/* FMIP - dieselbe Optik wie im Ticket. */
.fmip {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
}

.fmip-active   { background: rgba(220, 53, 69, 0.10); color: #dc3545; }
.fmip-inactive { background: rgba(40, 167, 69, 0.12); color: #1e7e34; }
.fmip-unknown  { background: rgba(108, 117, 125, 0.12); color: #6c757d; }

.fmip-dd {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.fmip-checked {
  color: #6c757d;
  font-size: 13px;
}

.imei-check-device {
  color: #1e7e34;
  font-weight: 600;
}

.detail-field {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 0.5rem;
  padding: 0.35rem 0;
  border-bottom: 1px solid #f1f3f5;
}

.detail-field:last-child {
  border-bottom: none;
}

.detail-field dt {
  font-weight: 600;
  color: #6c757d;
}

.detail-field dd {
  margin: 0;
}

.detail-muted {
  color: #6c757d;
}

.slip-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.15rem 0;
}

.slip-number {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.slip-error {
  margin-top: 0.25rem;
}

.error {
  color: #dc3545;
}

.detail-actions {
  margin-top: 1.25rem;
  padding-top: 0.85rem;
  border-top: 1px solid #dee2e6;
}

.detail-actions-title {
  margin: 0 0 0.6rem;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6c757d;
}

/* Aktionen nebeneinander - unter zwei Spalten ist Platz dafuer. */
.detail-actions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.detail-action-btn {
  text-align: left;
}

/* „Gerät korrigieren" greift nachträglich in Bestandsdaten ein und ist keine
   Routineaktion — der Akzentrahmen (Handyshop-Gelb) hebt sie aus der Liste heraus,
   ohne sie wie eine Gefahr (rot) aussehen zu lassen. */
.detail-action-correct {
  border: 1px solid var(--accent, #f7a600);
  background: transparent;
  color: var(--accent-hover, #e09600);
  font-weight: 600;
}

.detail-action-correct:hover,
.detail-action-correct:focus-visible {
  background: var(--accent-soft, rgba(247, 166, 0, 0.10));
  border-color: var(--accent-hover, #e09600);
  color: var(--accent-hover, #e09600);
}

.detail-actions-hint {
  margin: 0.5rem 0 0;
  font-size: 0.88rem;
  color: #6c757d;
}
</style>
